import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all payment modes
export const fetchPaymentModes = createAsyncThunk(
  "paymentModes/fetchPaymentModes",
  async () => {
    const res = await http.get("/dashboard/payment-modes");
    console.log("Payment Modes API Response:", res);
    console.log("Payment Modes data:", res.data);
    return res;
  }
);

// Create payment mode
export const createPaymentMode = createAsyncThunk(
  "paymentModes/createPaymentMode",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/payment-modes", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response.data || "Failed to create payment mode"
      );
    }
  }
);

// Fetch payment mode by UUID
export const fetchPaymentModeById = createAsyncThunk(
  "paymentModes/fetchPaymentModeById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/payment-modes/${uuid}`);
      return res;
    } catch (err) {
    
      return rejectWithValue(
        err.response?.data || "Failed to fetch payment mode details"
      );
    }
  }
);

// Update payment mode
export const updatePaymentMode = createAsyncThunk(
  "paymentModes/updatePaymentMode",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/dashboard/payment-modes/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update payment mode"
      );
    }
  }
);

// Delete payment mode
export const deletePaymentMode = createAsyncThunk(
  "paymentModes/deletePaymentMode",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/payment-modes/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete payment mode"
      );
    }
  }
);

// Toggle payment mode status
export const togglePaymentModeStatus = createAsyncThunk(
  "paymentModes/togglePaymentModeStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/payment-modes/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle payment mode status"
      );
    }
  }
);

const paymentModesSlice = createSlice({
  name: "paymentModes",
  initialState: {
    paymentModes: [],
    pagination: null,
    paymentMode: null,
    paymentModeCreateStatus: "idle",
    paymentModeUpdateStatus: "idle",
    paymentModeDeleteStatus: "idle",
    paymentModeFetchStatus: "idle",
    paymentModeStatusChangeStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetPaymentModeStatuses: (state) => {
      state.paymentModeCreateStatus = "idle";
      state.paymentModeUpdateStatus = "idle";
      state.paymentModeDeleteStatus = "idle";
      state.paymentModeStatusChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch payment modes
      .addCase(fetchPaymentModes.pending, (state) => {
        console.log("fetchPaymentModes.pending - Loading payment modes...");
        state.loading = true;
      })
      .addCase(fetchPaymentModes.fulfilled, (state, action) => {
        console.log("fetchPaymentModes.fulfilled - Full payload:", action.payload);
        console.log("fetchPaymentModes.fulfilled - Data array:", action.payload?.data);
        state.paymentModes = action.payload?.data || [];
        state.pagination = action.payload?.pagination || null;
        state.loading = false;
        console.log("fetchPaymentModes.fulfilled - State paymentModes after update:", state.paymentModes);
      })
      .addCase(fetchPaymentModes.rejected, (state, action) => {
        console.log("fetchPaymentModes.rejected - Error:", action.error);
        state.loading = false;
        state.error = action.error.message;
      })

      // Create payment mode
      .addCase(createPaymentMode.pending, (state) => {
        state.paymentModeCreateStatus = "loading";
      })
      .addCase(createPaymentMode.fulfilled, (state, action) => {
        state.paymentModeCreateStatus = "success";
        state.paymentMode = action.payload.data;
      })
      .addCase(createPaymentMode.rejected, (state) => {
        state.paymentModeCreateStatus = "failed";
      })

      // Fetch payment mode by ID
      .addCase(fetchPaymentModeById.pending, (state) => {
        state.paymentModeFetchStatus = "loading";
      })
      .addCase(fetchPaymentModeById.fulfilled, (state, action) => {
        state.paymentModeFetchStatus = "success";
        state.paymentMode = action.payload.data;
      })
      .addCase(fetchPaymentModeById.rejected, (state) => {
        state.paymentModeFetchStatus = "failed";
        state.paymentMode = null;
      })

      // Update payment mode
      .addCase(updatePaymentMode.pending, (state) => {
        state.paymentModeUpdateStatus = "loading";
      })
      .addCase(updatePaymentMode.fulfilled, (state, action) => {
        state.paymentModeUpdateStatus = "success";
        const index = state.paymentModes.findIndex(
          (pm) => pm.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.paymentModes[index] = action.payload.data;
        }
        state.paymentMode = action.payload.data;
      })
      .addCase(updatePaymentMode.rejected, (state) => {
        state.paymentModeUpdateStatus = "failed";
      })

      // Delete payment mode
      .addCase(deletePaymentMode.pending, (state) => {
        state.paymentModeDeleteStatus = "loading";
      })
      .addCase(deletePaymentMode.fulfilled, (state, action) => {
        state.paymentModeDeleteStatus = "success";
        state.paymentModes = state.paymentModes.filter(
          (pm) => pm.uuid !== action.payload.uuid
        );
      })
      .addCase(deletePaymentMode.rejected, (state) => {
        state.paymentModeDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(togglePaymentModeStatus.pending, (state) => {
        state.paymentModeStatusChangeStatus = "loading";
      })
      .addCase(togglePaymentModeStatus.fulfilled, (state, action) => {
        state.paymentModeStatusChangeStatus = "success";
        const index = state.paymentModes.findIndex(
          (pm) => pm.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.paymentModes[index] = action.payload.data;
        }
      })
      .addCase(togglePaymentModeStatus.rejected, (state) => {
        state.paymentModeStatusChangeStatus = "failed";
      })

      .addMatcher(isPending, (state, action) => {
        console.log("isPending matcher triggered:", action.type);
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state, action) => {
        console.log("isFulfilled matcher triggered:", action.type);
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        console.log("isRejected matcher triggered:", action.type);
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetPaymentModeStatuses } = paymentModesSlice.actions;
export default paymentModesSlice.reducer;
